import { Controller } from "stimulus"
import IMask from "imask"
import { removeFieldErrorState, surfaceFieldError, finalizeForm } from "validators/validation_display"

// generic value used to mark a field as invalid (used in setCustomValidity)
// see app/javascript/validators/validation_display.js for more details
const INVALID_MARK = 'invalid'

export default class extends Controller {
  static targets = [
    "enrollSmsNotificationsInput",
    "ridpRbaInput",
    "disableSubmissionInput",
    "preferencesForm",
    "homePhone",
    "mobilePhone",
    "homeEmail",
    "workEmail",
    "submitButton",
    "mailPreference",
    "emailPreference",
    "textPreference",
    "yearsToRenew",
    "methodContainer",
    "contactMethodGroup",
    "mobilePhoneWarningModal"
  ]

  // Get the sms flag from the input.
  get isSmsFlagEnabled() {
    return this.hasEnrollSmsNotificationsInputTarget && this.enrollSmsNotificationsInputTarget.value === "true"
  }

  get isRidpRbaFlagEnabled() {
    return this.hasRidpRbaInputTarget && this.ridpRbaInputTarget.value === "true"
  }

  // Get the disable submission control from the input.
  // This control is used to drive whether or not the contact method validation disables the save button, 
  // allowing some flows to disable the save button when validation fails.
  get canDisableSubmission() {
    return this.hasDisableSubmissionInputTarget && this.disableSubmissionInputTarget.value === "true"
  }

  get validators() {
    if (!this._validators) {
      this._validators = {
        homeEmail: new PersonalEmailValidator(this.homeEmailTarget),
        mobilePhone: new MobilePhoneValidator(this.mobilePhoneTarget),
        homePhone: new HomePhoneValidator(this.homePhoneTarget)
      }
    }
    return this._validators
  }

  get hasMobilePhone() {
    return this.validators.mobilePhone.hasValue
  }

  get hasHomeEmail() {
    return this.validators.homeEmail.hasValue
  }

  get preferences() {
    return {
      mail: this.mailPreferenceTarget.checked,
      email: this.emailPreferenceTarget.checked,
      text: this.textPreferenceTarget.checked
    }
  }

  // gated on the accessible_form_validation feature flag (set on <html> by the layout)
  // when off, the legacy native reportValidity()/alert() flow runs unchanged
  get isAccessibleValidationEnabled() {
    return document.documentElement.dataset.accessibleFormValidation === 'true'
  }

  connect() {
    this.updateFormState(this.canDisableSubmission) // when the form can disable the save button, we should report validity on connection
    this.maskPhones()

    if (this.isAccessibleValidationEnabled) {
      // Re-validate as the user edits (once the form has been submitted once): fields the user
      // has fixed clear, while still-invalid fields and group rules stay surfaced. See
      // updateFormState — before the first submit nothing is surfaced yet.
      this._accessibleEditHandler = () => this.updateFormState(false)
      this.element.addEventListener('input', this._accessibleEditHandler)
    }
  }

  disconnect() {
    if (this._accessibleEditHandler) {
      this.element.removeEventListener('input', this._accessibleEditHandler)
      this._accessibleEditHandler = null
    }
  }

  updateFormState(reportErrors = true) {
    // run validators regardless (they also toggle `required` label state as a side effect)
    const emailResult = this.validateEmail()
    const textResult = this.validateText()
    const homePhoneResult = this.validateHomePhone()

    if (this.isAccessibleValidationEnabled) {
      // Once the form has been submitted (was-validated), re-validate on edit so a field the
      // user has just fixed clears while still-invalid fields and the cross-field group rule
      // stay surfaced — rather than wiping every surfaced error on any edit. Before the first
      // submit there is nothing surfaced yet, so keep the form pristine.
      if (this.preferencesFormTarget.classList.contains('was-validated')) {
        this.#applyAccessibleValidation(emailResult, textResult, homePhoneResult)
      } else {
        this.#markField(this.homeEmailTarget, { valid: true })
        this.#markField(this.mobilePhoneTarget, { valid: true })
        this.#markField(this.homePhoneTarget, { valid: true })
        this.#clearGroupError()
      }
    } else {
      this.homeEmailTarget.setCustomValidity(emailResult.valid ? '' : emailResult.message)
      this.mobilePhoneTarget.setCustomValidity(textResult.valid ? '' : textResult.message)
      this.homePhoneTarget.setCustomValidity(homePhoneResult.valid ? '' : homePhoneResult.message)
    }

    // On the accessible flow we never disable the submit button — submission is blocked
    // by #validateSubmissionAccessible (via finalizeForm), which surfaces inline errors and
    // scrolls to the first invalid field instead. Keep the button enabled so the user can
    // click Save and see all errors at once.
    if (this.canDisableSubmission) {
      if (this.isAccessibleValidationEnabled) {
        this.submitButtonTarget.disabled = false
        this.submitButtonTarget.classList.remove('disabled')
      } else {
        const hasErrors = !this.preferencesFormTarget.checkValidity()
        this.submitButtonTarget.disabled = hasErrors
        this.submitButtonTarget.classList.toggle('disabled', hasErrors)
      }
    }

    if (reportErrors && !this.isAccessibleValidationEnabled) {
      this.preferencesFormTarget.reportValidity()
    }
  }

  validateEmail() {
    const hasEmailContact = this.preferences.email

    let isMobilePhoneBlankOrBothFilled = (this.isSmsFlagEnabled && (!this.hasMobilePhone || this.hasHomeEmail))
    this.toggleFieldRequired(this.homeEmailTarget, hasEmailContact || isMobilePhoneBlankOrBothFilled)

    if (hasEmailContact) {
      return this.validators.homeEmail.validate()
    }
    return { valid: true }
  }

  validateText() {
    const hasTextContact = this.preferences.text
    let isHomeEmailBlankOrBothFilled = (this.isSmsFlagEnabled && (!this.hasHomeEmail || this.hasMobilePhone))
    this.toggleFieldRequired(this.mobilePhoneTarget, hasTextContact || isHomeEmailBlankOrBothFilled)

    if (hasTextContact) {
      return this.validators.mobilePhone.validate()
    }
    return this.validators.mobilePhone.validateFormat()
  }

  validateHomePhone() {
    return this.validators.homePhone.validate()
  }

  validateSubmission(event) {
    if (this.isAccessibleValidationEnabled) {
      this.#validateSubmissionAccessible(event)
      return
    }

    const mobilePhoneFormat = this.validators.mobilePhone.validateFormat()
    if (!mobilePhoneFormat.valid) {
      event.preventDefault();
      this.mobilePhoneTarget.setCustomValidity(mobilePhoneFormat.message);
      this.mobilePhoneTarget.reportValidity();
      return;
    }

    if (!this.preferencesFormTarget.checkValidity()) {
      event.preventDefault();
      this.preferencesFormTarget.reportValidity();
      return;
    }

    if (this.hasYearsToRenewTarget) {
      if (!this.yearsToRenewTarget.checkValidity()) {
        event.preventDefault();
        this.yearsToRenewTarget.reportValidity();
        return;
      }
    }

    if (this.hasMethodContainerTarget) {
      this.setDestroys()
    }

    const preferences = this.preferences

    if (this.isSmsFlagEnabled && this.homeEmailTarget.value.length == 0 && this.mobilePhoneTarget.value.length == 0) {
      event.preventDefault();
      alert('An email or mobile phone number is required.');
      this.mobilePhoneTarget.focus();
    } else if (!preferences.mail && !preferences.email && !preferences.text) {
      event.preventDefault();
      alert('A contact method is required to proceed. If selecting Text, you must also choose Email or Mail.');
    } else if (preferences.text && !preferences.mail && !preferences.email) {
      event.preventDefault();
      alert('Text cannot be your only contact method. If you select Text, you must also choose Email or Mail.');
    } else {
      if (this.mobilePhoneTarget.value.length == 0 && this.isRidpRbaFlagEnabled) {
        event.preventDefault();
        $(this.mobilePhoneWarningModalTarget).modal('show');
      } else {
        this.preferencesFormTarget.submit()
      }
    }
  }

  setDestroys() {
    this.methodContainerTargets.forEach(container => {
      let input = container.querySelector('input.full-width')
      let destroy = container.querySelector('input.destroy')
      
      if (input && destroy) {
        destroy.value = input.value.length === 0
      }
    })
  }

  maskPhones() {
    IMask(this.homePhoneTarget, { mask: "(000) 000-0000" })
    IMask(this.mobilePhoneTarget, { mask: "(000) 000-0000" })
  }

  toggleFieldRequired(fieldTarget, isRequired) {
    let label = fieldTarget.closest('div').querySelector('label')
    label.classList.toggle('required', isRequired)
  }

  // accessible submit: run the shared field + cross-field group validation, then let
  // finalizeForm surface every invalid field's .invalid-feedback and scroll/focus the first one
  // similar to form_validation_controller#submit
  #validateSubmissionAccessible(event) {
    this.#applyAccessibleValidation(this.validateEmail(), this.validateText(), this.validateHomePhone())

    if (this.hasMethodContainerTarget) {
      this.setDestroys()
    }

    if (!finalizeForm(this.preferencesFormTarget)) {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    // keep this check --> the RIDP flow still asks the user to confirm a missing mobile number before submit
    if (this.mobilePhoneTarget.value.length === 0 && this.isRidpRbaFlagEnabled) {
      event.preventDefault()
      $(this.mobilePhoneWarningModalTarget).modal('show')
    }
    // otherwise allow the form to submit natively
  }

  // shared field + cross-field group validation for the accessible flow.
  // mark each field from its validator result, then apply the contact-method group rules.
  // Called both on submit (#validateSubmissionAccessible) and live on edit (updateFormState)
  // so editing re-evaluates every rule instead of clearing all surfaced errors.
  #applyAccessibleValidation(emailResult, textResult, homePhoneResult) {
    this.#markField(this.homeEmailTarget, emailResult)
    this.#markField(this.mobilePhoneTarget, textResult)
    this.#markField(this.homePhoneTarget, homePhoneResult)

    // cross-field rules -> the contact-method group invalid-feedback (replaces the legacy alerts)
    // "No contact method selected" is checked first so it isn't pre-empted by the SMS email/mobile
    // rule when nothing is chosen
    this.#clearGroupError()
    const preferences = this.preferences
    if (!preferences.mail && !preferences.email && !preferences.text) {
      this.#applyGroupError('contact_method_required')
    } else if (this.isSmsFlagEnabled && this.homeEmailTarget.value.length === 0 && this.mobilePhoneTarget.value.length === 0) {
      this.#applyGroupError('email_or_mobile_required')
    } else if (preferences.text && !preferences.mail && !preferences.email) {
      this.#applyGroupError('text_requires_email_or_mail')
    }
  }

  // mark a field :invalid (or clear it) for the accessible flow.
  // sets data-error-code so feedbackFor can resolve the matching invalid-feedback, then surfaces
  // it (surfaceFieldError is idempotent, so finalizeForm re-surfacing on submit is a no-op)
  #markField(target, result) {
    removeFieldErrorState(target)
    if (!result.valid) {
      target.dataset.errorCode = result.code
      target.setCustomValidity(INVALID_MARK)
      surfaceFieldError(target)
    }
  }

  // contact-method preferences are a checkbox group
  // mark invalid and outline the whole group via .is-invalid-group rather than the
  // single mail checkbox —> otherwise an unrelated rule (e.g. "email or mobile required") lands a
  // red box on mail -- designed to more appropriately reveal the group feedback according to the contact
  // methods the user has checked
  #applyGroupError(code) {
    if (!this.hasMailPreferenceTarget) return
    this.mailPreferenceTarget.dataset.errorCode = code
    this.mailPreferenceTarget.setCustomValidity(INVALID_MARK)
    if (this.hasContactMethodGroupTarget) this.contactMethodGroupTarget.classList.add('is-invalid-group')
    surfaceFieldError(this.mailPreferenceTarget)
  }

  #clearGroupError() {
    if (!this.hasMailPreferenceTarget) return
    removeFieldErrorState(this.mailPreferenceTarget)
    if (this.hasContactMethodGroupTarget) this.contactMethodGroupTarget.classList.remove('is-invalid-group')
  }
}

class HomePhoneValidator {
  static REQUIRED_LENGTH = 10
  static ERRORS = {
    allZeros: { code: "home_phone_all_zeros", message: "Home Phone number cannot be all zeros." },
    beginWithZero: { code: "home_phone_begins_with_zero", message: "Phone numbers cannot begin with a 0. Please check the number you entered, remove any leading zeros, and resubmit." },
    invalidLength: { code: "home_phone_invalid_length", message: `Phone must be ${HomePhoneValidator.REQUIRED_LENGTH} digits long.` }
  }

  constructor(phoneTarget) {
    this.phoneTarget = phoneTarget
  }

  validate() {
    const phoneValue = this.phoneTarget.value.replace(/\D/g, '')

    if (phoneValue.length > 0) {
      if (/^0+$/.test(phoneValue)) {
        return { valid: false, ...HomePhoneValidator.ERRORS.allZeros }
      } else if(/^0\d+/.test(phoneValue)) {
        return { valid: false, ...HomePhoneValidator.ERRORS.beginWithZero }
      } else if (phoneValue.length < HomePhoneValidator.REQUIRED_LENGTH) {
        return { valid: false, ...HomePhoneValidator.ERRORS.invalidLength }
      }
    }
    return { valid: true }
  }
}

class PersonalEmailValidator {
  static ERRORS = {
    empty: { code: "home_email_required", message: "You must enter an email address to receive notices and updates by email." }
  }

  constructor(emailTarget) {
    this.emailTarget = emailTarget
  }

  get hasValue() {
    return this.emailTarget.value.trim().length > 0
  }

  validate() {
    const emailValue = this.emailTarget.value.trim()

    if (emailValue.length === 0) {
      return { valid: false, ...PersonalEmailValidator.ERRORS.empty }
    }
    return { valid: true }
  }
}

class MobilePhoneValidator {
  static REQUIRED_LENGTH = 10
  static ERRORS = {
    allZeros: { code: "mobile_phone_all_zeros", message: "Mobile Phone number cannot be all zeros." },
    beginWithZero: { code: "mobile_phone_begins_with_zero", message: "Phone numbers cannot begin with a 0. Please check the number you entered, remove any leading zeros, and resubmit." },
    invalidLength: { code: "mobile_phone_required", message: "You must enter a mobile phone number to receive notices and updates by text." }
  }

  constructor(phoneTarget) {
    this.phoneTarget = phoneTarget
  }

  get hasValue() {
    return this.phoneTarget.value.length > 0
  }

  validate() {
    const phoneValue = this.phoneTarget.value.replace(/\D/g, '')

    if (/^0+$/.test(phoneValue)) {
      return { valid: false, ...MobilePhoneValidator.ERRORS.allZeros }
    } else if(/^0\d+/.test(phoneValue)) {
      return { valid: false, ...MobilePhoneValidator.ERRORS.beginWithZero }
    } else if (phoneValue.length < 1 || phoneValue.length < MobilePhoneValidator.REQUIRED_LENGTH) {
      return { valid: false, ...MobilePhoneValidator.ERRORS.invalidLength }
    }
    return { valid: true }
  }

  validateFormat() {
    const phoneValue = this.phoneTarget.value.replace(/\D/g, '')
    if (phoneValue.length < 1) return { valid: true }
    if (/^0+$/.test(phoneValue)) {
      return { valid: false, ...MobilePhoneValidator.ERRORS.allZeros }
    } else if (/^0\d+/.test(phoneValue)) {
      return { valid: false, ...MobilePhoneValidator.ERRORS.beginWithZero }
    }
    return { valid: true }
  }
}
